import { useMutation } from '@tanstack/react-query'
import type { SubmitEvent } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { describeApiError } from '@/api/problemDetails'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  toast,
} from '@/components/ui'
import { changePassword } from '@/features/auth/api'

const MIN_PASSWORD_LENGTH = 12

interface FieldErrors {
  currentPassword?: string
  newPassword?: string
  confirmPassword?: string
}

/**
 * Card de Segurança: troca de senha do usuário logado (POST /auth/password/change). Valida no cliente
 * (senha atual informada, nova com o mínimo de caracteres e confirmação coincidente) e mostra os erros
 * da Api (ex.: "senha atual incorreta") em um alerta. Em sucesso, limpa o formulário e avisa por toast.
 */
export function SecurityCard() {
  const { t } = useTranslation()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

  const mutation = useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      toast.success(t('settings.security.success'))
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setFieldErrors({})
    },
  })

  function validate(): FieldErrors {
    const errors: FieldErrors = {}

    if (currentPassword.length === 0) {
      errors.currentPassword = t('settings.security.errors.currentRequired')
    }

    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      errors.newPassword = t('settings.security.errors.newLength')
    }

    if (confirmPassword !== newPassword) {
      errors.confirmPassword = t('settings.security.errors.mismatch')
    }

    return errors
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault()
    const errors = validate()
    setFieldErrors(errors)

    if (Object.keys(errors).length > 0) {
      return
    }

    mutation.mutate({ currentPassword, newPassword })
  }

  function clearApiError(): void {
    if (mutation.isError) {
      mutation.reset()
    }
  }

  const apiMessages = describeApiError(mutation.error)
  const [firstMessage, ...restMessages] = apiMessages

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.security.title')}</CardTitle>
        <CardDescription>{t('settings.security.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(event) => {
            handleSubmit(event)
          }}
          className="flex flex-col gap-4"
          noValidate
        >
          {firstMessage !== undefined ? (
            <Alert variant="danger" title={firstMessage}>
              {restMessages.length > 0 ? (
                <ul className="list-disc pl-4">
                  {restMessages.map((message) => (
                    <li key={message}>{message}</li>
                  ))}
                </ul>
              ) : null}
            </Alert>
          ) : null}
          <Input
            id="settings-current-password"
            type="password"
            label={t('settings.security.currentLabel')}
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(event) => {
              setCurrentPassword(event.target.value)
              clearApiError()
            }}
            error={fieldErrors.currentPassword}
          />
          <Input
            id="settings-new-password"
            type="password"
            label={t('settings.security.newLabel')}
            hint={t('settings.security.newHint')}
            autoComplete="new-password"
            required
            value={newPassword}
            onChange={(event) => {
              setNewPassword(event.target.value)
              clearApiError()
            }}
            error={fieldErrors.newPassword}
          />
          <Input
            id="settings-confirm-password"
            type="password"
            label={t('settings.security.confirmLabel')}
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(event) => {
              setConfirmPassword(event.target.value)
              clearApiError()
            }}
            error={fieldErrors.confirmPassword}
          />
          <div>
            <Button type="submit" loading={mutation.isPending}>
              {t('settings.security.submit')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
